import { Injectable } from "../../../lib/web/reflection/injectable";
import { SessionStorageService } from "../../../lib/web/services/session-storage.service";
import { ApiService } from "./api.service";
import { SessionService } from "./session.service";

interface UserFavoriteData {
    time: number;
    data: UserFavorite[];
}

interface UserFavorite {
    id?: number;
    type?: 'capsule' | 'collection' | 'partner-landing';
}

@Injectable({ type: 'singleton' })
export class UserFavoriteService {

    public constructor(private _sessionStorageService: SessionStorageService,
        private _apiService: ApiService, 
        private _sessionService: SessionService) {
    }

    public init(): void {
        if (this._sessionService.isSigned) {
            this.load();
        }
        else {
            this.clear();
        }
    }

    private async load(): Promise<void> {
        const favorites: UserFavoriteData = this.get();        
        const { time } = favorites;
        if (!time || new Date(time).addHours(CONFIG.USER_FAVORITES_RESET_HOURS) < new Date()) {
            const response: any[] = await this._apiService.get('/api/v1/user/favorites');
            this.set({
                time: new Date().getTime(),
                data: response
            });
        }
        else {
            this.changed();
        }
    }

    private changed(): void {
        window.dispatchEvent(new CustomEvent('user-favorites-changed'));
    }

    private clear(): void {
        this._sessionStorageService.remove('user-favorites');
        this.changed();
    }

    private get(): UserFavoriteData {
        return this._sessionStorageService.get('user-favorites') || { data: [] };
    }

    private set(data: UserFavoriteData): void {
        this._sessionStorageService.set('user-favorites', data);
        this.changed();
    }

    public isFavorite(id: number, type: 'capsule' | 'collection' | 'partner-landing'): boolean {
        let isFavorite: boolean = false;
        if (this._sessionService.isSigned) {
            const data: UserFavorite[] = this.get().data || [];            
            isFavorite = data.some(f => f.id == id && f.type == type);
        }
        return isFavorite;
    }

    public getItem(id: number, type: 'capsule' | 'collection' | 'partner-landing'): UserFavorite {
        let item: UserFavorite = null;
        if (this._sessionService.isSigned) {
            const data: UserFavorite[] = this.get().data || [];
            item = data.find(f => f.id == id && f.type == type);
        }
        return item;
    }

    private async updateItem(id: number, type: 'capsule' | 'collection' | 'partner-landing', action: 'add' | 'remove'): Promise<void> {
        const data: UserFavoriteData = this.get();
        const item: UserFavorite = data.data.find(f => f.id == id && f.type == type);
        if (action === 'add' && !item) {
            data.data.push({ id, type });
            this.set(data);
        }
        else if (action === 'remove' && item) {            
            data.data = data.data.filter(f => !(f.id == id && f.type == type));
            this.set(data);
        }
    }

    public async add(id: number, type: 'capsule' | 'collection' | 'partner-landing'): Promise<void> {
        await this.updateItem(id, type, 'add');
        await this._apiService.post(`/api/v1/user/favorite/${id}/${type}`);
    }

    public async remove(id: number, type: 'capsule' | 'collection' | 'partner-landing'): Promise<void> {
        await this.updateItem(id, type, 'remove');
        await this._apiService.delete(`/api/v1/user/favorite/${id}/${type}`);
    }
}